import { cn, loadDomAnimation } from '@bridebook/aura';
import { PlannerBubble } from '@bridebook/web/components/ai-planner/planner-bubble';
import { PlannerFab } from '@bridebook/web/components/ai-planner/planner-fab';
import { PLANNER_FACE_HOVER_CLASS } from '@bridebook/web/components/ai-planner/planner-face';
import { PlannerTile } from '@bridebook/web/components/ai-planner/planner-tile';
import {
  AI_PLANNER_EASE,
  AI_PLANNER_FAB_ID,
  AI_PLANNER_Z_INDEX,
} from '@bridebook/web/lib/ai-planner/constants';
import { PLANNER_TEST_IDS } from '@bridebook/web/lib/ai-planner/test-ids';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import {
  getAIPlannerUnreadReplies,
  getIsAIPlannerAvailable,
  getIsAIPlannerPanelOpen,
  getIsAIPlannerPanelRestored,
} from '@bridebook/web/lib/ai-planner/selectors';
import { useIsDialogOpen } from '@bridebook/web/lib/ai-planner/hooks/use-is-dialog-open';
import { usePlannerOpened } from '@bridebook/web/lib/ai-planner/hooks/use-planner-opened';
import { usePlannerAnalytics } from '@bridebook/web/lib/ai-planner/hooks/use-planner-analytics';
import { openAIPlanner } from '@bridebook/web/lib/ai-planner/slice';
import { MOBILE_NAV_HEIGHT } from '@bridebook/web/lib/mobile-app/constants';
import { selectIsAnyOnboardingPage } from '@bridebook/web/lib/onboarding/selectors';
import { STICKY_BOTTOM_BAR_HEIGHT } from '@bridebook/web/lib/supplier/constants';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { getIsAnyBlockingPopupVisible } from '@bridebook/web/lib/ui/get-is-any-blocking-popup-visible';
import { getIsCsatFeedbackOpen, getStickyBottomBarShown } from '@bridebook/web/lib/ui/selectors';
import { LazyMotion, m, type Variants } from 'motion/react';
import { useTranslation } from 'next-i18next';
import { type RefObject, useEffect, useRef, useState } from 'react';

// Shrinks as the planner opens from it, and springs back after it closes.
// A planner restored from an earlier visit hides it at once.
const fabVariants: Variants = {
  hidden: (isRestored: boolean) => ({
    opacity: 0,
    scale: 0.62,
    transition: isRestored
      ? { duration: 0 }
      : {
          opacity: { duration: 0.14, ease: 'easeIn' },
          scale: { duration: 0.18, ease: 'easeIn' },
        },
  }),
  shown: {
    opacity: 1,
    scale: 1,
    transition: {
      opacity: { duration: 0.18, ease: 'easeOut', delay: 0.1 },
      scale: { duration: 0.34, ease: [0.3, 1.42, 0.5, 1], delay: 0.1 },
    },
  },
};

// The desktop button hides as the panel slides in, and returns after it closes
const desktopFabVariants: Variants = {
  hidden: (isRestored: boolean) => ({
    opacity: 0,
    scale: 0.9,
    transition: isRestored
      ? { duration: 0 }
      : {
          opacity: { duration: 0.14, ease: 'easeIn' },
          scale: { duration: 0.18, ease: 'easeIn' },
        },
  }),
  shown: {
    opacity: 1,
    scale: 1,
    transition: {
      opacity: { duration: 0.22, ease: 'easeOut', delay: 0.14 },
      scale: { duration: 0.28, ease: AI_PLANNER_EASE.dock, delay: 0.14 },
    },
  },
};

// Timings after a close: when the button has settled, and when the pulse runs
const FAB_SETTLED_MS = 440;
const PULSE_DELAY_MS = 260;
const PULSE_DURATION_MS = 720;

/**
 * The button's state during the planner's open and close animation. While the planner is
 * open, and until the button settles back, it sits above the planner.
 * Right after a close it pulses once.
 */
const useFabTransition = (isPanelOpen: boolean) => {
  const [isRaised, setIsRaised] = useState(isPanelOpen);
  const [isPulsing, setIsPulsing] = useState(false);
  const wasPanelOpen = useRef(isPanelOpen);

  useEffect(() => {
    const wasOpen = wasPanelOpen.current;
    wasPanelOpen.current = isPanelOpen;

    if (isPanelOpen) {
      setIsRaised(true);
      setIsPulsing(false);
      return;
    }
    // Nothing to settle when the button mounts with the planner closed
    if (!wasOpen) return;

    const timers = [
      setTimeout(() => setIsRaised(false), FAB_SETTLED_MS),
      setTimeout(() => setIsPulsing(true), PULSE_DELAY_MS),
      setTimeout(() => setIsPulsing(false), PULSE_DELAY_MS + PULSE_DURATION_MS),
    ];
    return () => timers.forEach(clearTimeout);
  }, [isPanelOpen]);

  return { isRaised, isPulsing };
};

type MobileButtonProps = {
  buttonRef: RefObject<HTMLButtonElement>;
  isPanelOpen: boolean;
  isRestored: boolean;
  unreadReplies: number;
  onOpen: () => void;
  label: string;
  bottom: string;
  greeting: string;
  isGreeting: boolean;
  isPinging: boolean;
};

// Round mobile entry point. It stays mounted while the planner is open, because the
// planner opens from it and closes back into it.
const MobileButton = ({
  buttonRef,
  isPanelOpen,
  isRestored,
  unreadReplies,
  onOpen,
  label,
  bottom,
  greeting,
  isGreeting,
  isPinging,
}: MobileButtonProps) => {
  const { isRaised, isPulsing } = useFabTransition(isPanelOpen);
  const hasNews = !isPanelOpen && unreadReplies > 0;
  const isResting = !isPanelOpen && !hasNews;

  return (
    <LazyMotion features={loadDomAnimation}>
      <m.button
        ref={buttonRef}
        id={AI_PLANNER_FAB_ID}
        data-test={PLANNER_TEST_IDS.fab}
        type="button"
        onClick={onOpen}
        aria-label={label}
        aria-hidden={isPanelOpen || undefined}
        tabIndex={isPanelOpen ? -1 : undefined}
        // Sits above the bottom tab bar, and moves up when a sticky bar slides in
        style={{
          bottom,
          zIndex: isRaised ? AI_PLANNER_Z_INDEX.entryRaised : AI_PLANNER_Z_INDEX.entry,
        }}
        className={cn(
          'fixed right-4 size-12 rounded-full transition-[bottom] duration-150 ease-in-out',
          PLANNER_FACE_HOVER_CLASS,
          isPanelOpen && 'pointer-events-none',
        )}
        variants={fabVariants}
        custom={isRestored}
        initial={false}
        animate={isPanelOpen ? 'hidden' : 'shown'}>
        {/* With news it hops with a badge, otherwise at rest the ring turns and BB looks around */}
        <PlannerFab
          isSpinning={isResting}
          look={isResting ? 'wander' : undefined}
          idle={hasNews ? 'nudge' : undefined}
          badge={unreadReplies}
          isPulsing={isPulsing}
          isPinging={isPinging}
        />
        <PlannerBubble message={greeting} isShown={isGreeting} onClick={onOpen} />
      </m.button>
    </LazyMotion>
  );
};

type DesktopButtonProps = {
  buttonRef: RefObject<HTMLButtonElement>;
  isPanelOpen: boolean;
  isRestored: boolean;
  unreadReplies: number;
  onOpen: () => void;
  label: string;
  greeting: string;
  isGreeting: boolean;
  isPinging: boolean;
};

// Round desktop entry point in the corner. It grows into a tile to greet, and hides
// while the panel is open.
const DesktopButton = ({
  buttonRef,
  isPanelOpen,
  isRestored,
  unreadReplies,
  onOpen,
  label,
  greeting,
  isGreeting,
  isPinging,
}: DesktopButtonProps) => {
  const { isPulsing } = useFabTransition(isPanelOpen);
  const hasNews = !isPanelOpen && unreadReplies > 0;
  const isResting = !isPanelOpen && !hasNews;

  return (
    <LazyMotion features={loadDomAnimation}>
      <m.button
        ref={buttonRef}
        data-test={PLANNER_TEST_IDS.fab}
        type="button"
        onClick={onOpen}
        aria-label={label}
        aria-hidden={isPanelOpen || undefined}
        tabIndex={isPanelOpen ? -1 : undefined}
        style={{ zIndex: AI_PLANNER_Z_INDEX.entry }}
        className={cn(
          'fixed right-6 bottom-6 cursor-pointer',
          PLANNER_FACE_HOVER_CLASS,
          isGreeting ? 'rounded-2xl' : 'rounded-full',
          isPanelOpen && 'pointer-events-none',
        )}
        variants={desktopFabVariants}
        custom={isRestored}
        initial={false}
        animate={isPanelOpen ? 'hidden' : 'shown'}>
        <PlannerTile message={greeting} isShown={isGreeting}>
          {/* With news it hops with a badge, otherwise at rest the ring turns and BB looks around */}
          <PlannerFab
            isSpinning={isResting}
            look={isResting ? 'wander' : undefined}
            idle={hasNews ? 'nudge' : undefined}
            badge={unreadReplies}
            isPulsing={isPulsing}
            isPinging={isPinging}
          />
        </PlannerTile>
      </m.button>
    </LazyMotion>
  );
};

// Wait after the button shows before the bubble pops up
const GREETING_DELAY_MS = 900;
// Wait between ping rings, and how long the rings run
const PING_EVERY_MS = 10000;
const PING_MS = 2000;
// How long the bubble takes to shrink away, so the first rings follow it
const GREETING_EXIT_MS = 220;
// Set once the greeting has played, so it does so once per app visit, not per page
let hasGreeted = false;

/**
 * Attention cues until the couple first opens the planner: a greeting bubble shortly after
 * the first show in a visit, then ping rings at an interval. A tap anywhere but the button
 * hides the greeting, starts the first rings at once and restarts the interval.
 */
const useAttention = (isEnabled: boolean, isPanelOpen: boolean, isPlannerPage: boolean) => {
  const { hasOpenedPlanner, markPlannerOpened } = usePlannerOpened();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isGreeting, setIsGreeting] = useState(false);
  const [isPinging, setIsPinging] = useState(false);
  const [lastTap, setLastTap] = useState({ at: 0, endedGreeting: false });
  const isGreetingRef = useRef(isGreeting);
  isGreetingRef.current = isGreeting;

  // Opening the planner, as the panel or its page, stops the cues for good
  useEffect(() => {
    if (!(isPanelOpen || isPlannerPage)) return;
    markPlannerOpened();
    setIsGreeting(false);
  }, [isPanelOpen, isPlannerPage, markPlannerOpened]);

  // Any open modal on the page, tracked in the store or not, pauses the cues
  const isDialogOpen = useIsDialogOpen(isEnabled && !hasOpenedPlanner);
  const isActive = isEnabled && !hasOpenedPlanner && !isDialogOpen;

  // Hides the greeting when a popup opens, and plays it again after the popup closes
  useEffect(() => {
    if (isActive || !isGreetingRef.current) return;
    hasGreeted = false;
    setIsGreeting(false);
  }, [isActive]);

  useEffect(() => {
    if (!isActive || isPanelOpen || hasGreeted) return;

    const timer = setTimeout(() => {
      hasGreeted = true;
      setIsGreeting(true);
    }, GREETING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isActive, isPanelOpen]);

  useEffect(() => {
    if (!isActive) return;
    const onTap = (event: PointerEvent) => {
      // A press on the button is not a tap away: its click opens the planner
      if (buttonRef.current?.contains(event.target as Node)) return;
      setLastTap({ at: Date.now(), endedGreeting: isGreetingRef.current });
      setIsGreeting(false);
    };
    document.addEventListener('pointerdown', onTap);
    return () => document.removeEventListener('pointerdown', onTap);
  }, [isActive]);

  // Repeats the rings at an interval while the planner is closed
  useEffect(() => {
    setIsPinging(false);
    if (!isActive || isPanelOpen) return;

    let timer: ReturnType<typeof setTimeout>;
    const ping = () => {
      setIsPinging(true);
      timer = setTimeout(() => {
        setIsPinging(false);
        timer = setTimeout(ping, PING_EVERY_MS);
      }, PING_MS);
    };
    timer = setTimeout(ping, lastTap.endedGreeting ? GREETING_EXIT_MS : PING_EVERY_MS);
    return () => clearTimeout(timer);
  }, [isActive, isPanelOpen, lastTap]);

  return { buttonRef, isGreeting, isPinging };
};

/**
 * Floating entry point for the planner: a round button in the bottom-right corner,
 * above the tab bar on mobile.
 * It hides while the panel is open, as the panel has its own close button.
 */
export const AIPlannerButton = () => {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const pathname = usePathWithoutMarket();
  const isAvailable = useSelector(getIsAIPlannerAvailable);
  const isPanelOpen = useSelector(getIsAIPlannerPanelOpen);
  const isPanelRestored = useSelector(getIsAIPlannerPanelRestored);
  const { isDesktop } = useSelector(getScreenType);
  const mobileNavShown = useSelector((state) => state.ui.mobileNavShown);
  const stickyBottomBarShown = useSelector(getStickyBottomBarShown);
  const appKeyboardOpened = useSelector((state) => state.mobileapp.appKeyboardOpened);
  const unreadReplies = useSelector(getAIPlannerUnreadReplies);
  const { clickedFloatingButtonAnalytics } = usePlannerAnalytics();
  // The planner page already shows the chat, so the button is not needed there.
  const isPlannerPage = pathname.startsWith(UrlHelper.planner);
  // Onboarding has its own flow or the planner is in non-dismissable mode, so the button stays out of it
  const isOnboardingPage = useSelector(selectIsAnyOnboardingPage);
  // The coach's knowledge is UK-only, so it is offered nowhere else.
  const isHidden = !isAvailable || appKeyboardOpened || isPlannerPage || isOnboardingPage;
  // A modal, popup or the feedback drawer pauses the greeting and rings until it closes
  const isPopupOpen = useSelector(
    (state) => getIsAnyBlockingPopupVisible(state) || getIsCsatFeedbackOpen(state),
  );
  const { buttonRef, isGreeting, isPinging } = useAttention(
    !isHidden && !isPopupOpen,
    isPanelOpen,
    isPlannerPage,
  );

  if (isHidden) return null;

  const open = () => {
    dispatch(openAIPlanner());
    clickedFloatingButtonAnalytics();
  };

  if (isDesktop) {
    return (
      <DesktopButton
        buttonRef={buttonRef}
        isPanelOpen={isPanelOpen}
        isRestored={isPanelRestored}
        unreadReplies={unreadReplies}
        onOpen={open}
        label={t('aiPlanner.cta')}
        greeting={t('aiPlanner.greeting')}
        isGreeting={isGreeting}
        isPinging={isPinging}
      />
    );
  }

  return (
    <MobileButton
      buttonRef={buttonRef}
      isPanelOpen={isPanelOpen}
      isRestored={isPanelRestored}
      unreadReplies={unreadReplies}
      onOpen={open}
      label={t('aiPlanner.cta')}
      bottom={`calc(${(mobileNavShown ? MOBILE_NAV_HEIGHT : 0) + (stickyBottomBarShown ? STICKY_BOTTOM_BAR_HEIGHT : 0)}px + 16px + env(safe-area-inset-bottom))`}
      greeting={t('aiPlanner.greeting')}
      isGreeting={isGreeting}
      isPinging={isPinging}
    />
  );
};
